import React, { Suspense, useContext, useEffect } from 'react';
import { Grid, useMediaQuery } from '@material-ui/core';
import {
    useRouteMatch,
    Switch,
    Route,
    useLocation,
    Redirect,
    matchPath,
    useHistory
} from 'react-router-dom';
import { lazyWithPreload } from '../../utilities/RLazyLoading';
import { LoginProtectedRoute } from '../Core/Routes/LoginProtectedRoute';
import AirportStep from '../Aircraft/AirportStep';
import AircraftStep from '../Aircraft/AircraftStep';
import FlowTitle from './FlowTitle';
import SummaryStep from '../Summary/SummaryStep';
import PilotList from '../Pilot/PilotList';
import PilotsProtectedRoute from '../Core/Routes/PilotsProtectedRoute';
import { CoveragesStep } from './Coverages/CoveragesStep';
import { CheckoutStep } from '../Payment/CheckoutStep';
import EndOfFlowPage from './EndOfFlowPage';
import PaymentContext from '../../context/PaymentContext/PaymentContext';
import SkywatchStepper from '../Core/Steppers/SkywatchStepper';
import { QuoteContextStore } from '../../context/QuoteContext/QuoteContext';
import { LayoutMain } from '../Layout/LayoutMain';
import './PurchaseFlow.scss';
import ScrollToTop from '../Core/Behavioral/ScrollToTop';
import {SupportPhoneCall} from '../SideMenu/AppBarItems/SupportPhoneCall';
import FlexView from 'react-flexview/lib'
import usePurchaseFlowParams from './hooks/usePurchaseFlowParams';


const ProfileStep = lazyWithPreload(() =>
    import('../Profile/ProfileStep').then((module) => ({
        default: module.ProfileStep
    }))
);
const PilotStep = lazyWithPreload(() =>
    import('../Pilot/PilotStep').then((module) => ({
        default: module.PilotStep
    }))
);
const PolicySummary = lazyWithPreload(() =>
    import('./PriceWidgets/PolicySummary').then((module) => ({
default: module.PolicySummary
    }))
);

export const PurchaseFlow = () => {
    const { path, url } = useRouteMatch();
    const location = useLocation();
    const isTablet = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('mobile'));
    const { shouldShowPriceWidgit, loadQuote, loadMemberships, goBackBrowser,locked_quote } = useContext(QuoteContextStore);
    const history = useHistory();
    const { quoteId } = usePurchaseFlowParams();

    const isMobileQuery = useMediaQuery((theme) =>
        theme.breakpoints.down('sm')
    );

    useEffect(() => {
        const event = history.listen(location => {
            if(history.action === "POP") {
                goBackBrowser(location.pathname, quoteId,locked_quote);
            }
        });

        if(quoteId) {
            loadQuote(quoteId, true);
        }

        return () => {
            event();
        }
    },[quoteId]);

    useEffect(() => {
        setTimeout(() => {
            ProfileStep.preload();
            PilotStep.preload();
            PolicySummary.preload();
        }, 500);

        loadMemberships()
    }, []);


    return (
        <Switch>
            {!location.pathname.includes('congratulations') &&
                !location.pathname.includes('wearealmostthere') &&
                !location.pathname.includes('savechangesrenewal') && 
                !location.pathname.includes('notcleared') && 
                !location.pathname.includes('underwritterrefferal') && 
                !location.pathname.includes('declined') && (
                    <>
                        <LayoutMain
                            sideComponent={
                                <SkywatchStepper horizontal={isMobileQuery} />
                            }
                        >
                        <FlexView>
                            <FlowTitle />
                            <SupportPhoneCall className="support-call-desktop"/>
                        </FlexView>
                            <Grid
                                container
                                direction={isMobile ? 'column' : 'row'}
                            >
                                <Grid item xs>
                                    <Route exact path={url}>
                                        <Redirect exact to={`${url}/profile`} />
                                    </Route>

                                    <LoginProtectedRoute
                                        redirectTo="/insurance/profile"
                                        path={`${path}/profile/:quoteId?`}
                                    >
                                        <Suspense fallback={<div />}>
                                            <ScrollToTop>
                                                <ProfileStep />
                                            </ScrollToTop>
                                        </Suspense>
                                    </LoginProtectedRoute>

                                    <Route path={`${path}/aircraft/:quoteId`}>
                                        <ScrollToTop>
                                            <AircraftStep />
                                        </ScrollToTop>
                                    </Route>

                                    <Route path={`${path}/airport/:quoteId`}>
                                        <ScrollToTop>
                                            <AirportStep />
                                        </ScrollToTop>
                                    </Route>

                                    <Route path={`${path}/pilots/:quoteId`}>
                                        <Suspense fallback={<div />}>
                                            <ScrollToTop>
                                                <PilotStep />
                                            </ScrollToTop>
                                        </Suspense>
                                    </Route>

                                    <PilotsProtectedRoute
                                        path={`${path}/pilot-list/:quoteId`}
                                        quoteId={quoteId}
                                    >
                                        <ScrollToTop>
                                            <PilotList />
                                        </ScrollToTop>
                                    </PilotsProtectedRoute>

                                    <Route path={`${path}/coverages/:quoteId`}>
                                        <ScrollToTop>
                                            <CoveragesStep />
                                        </ScrollToTop>
                                    </Route>

                                    <Route path={`${path}/summary/:quoteId`}>
                                        <ScrollToTop>
                                            <SummaryStep />
                                        </ScrollToTop>
                                    </Route>

                                    <Route path={`${path}/checkout/:quoteId`}>
                                        <PaymentContext>
                                            <ScrollToTop>
                                                <CheckoutStep inFlow={true}/>
                                            </ScrollToTop>
                                        </PaymentContext>
                                    </Route>
                                </Grid>
                                <Grid item>
                                    {!isTablet && shouldShowPriceWidgit && (
                                            <Suspense fallback={<div />}>
                                                <PolicySummary />
                                            </Suspense>
                                        )}
                                </Grid>
                            </Grid>
                        </LayoutMain>
                    </>
                )}
            <Route path={`${path}/wearealmostthere/:quoteId?`}>
                <EndOfFlowPage />
            </Route>
            <Route path={`${path}/declined/:quoteId?`}>
                <EndOfFlowPage />
            </Route>
            <Route path={`${path}/underwritterrefferal/:quoteId?`}>
                <EndOfFlowPage />
            </Route>
            <Route path={`${path}/savechangesrenewal/:quoteId?`}>
                <EndOfFlowPage />
            </Route>
            <Route path={`${path}/notcleared/:quoteId?`}>
                <EndOfFlowPage />
            </Route>
            <Route path={`${path}/congratulations/:quoteId?`}>
                <Suspense fallback={<div />}>
                    <EndOfFlowPage />
                </Suspense>
            </Route>
        </Switch>
    );
};
